import Animation from "./Animation";
import type {
    EndAnimationListener,
    UpdateListener,
    UpdateState,
} from "./interfaces";

const DEFAULT_ANIMATION_VALUE = 0;
const DEFAULT_ANIMATION_DURATION = 400;

class Animator {
    public time: number;

    private animations: Animation[];
    private listeners: UpdateListener[];
    private endListeners: EndAnimationListener[];
    private updateState: UpdateState;
    private requestId: number;
    private needUpdate: boolean;
    private trackedMap: Map<string, Animation[]>;
    private trackTags: string[];
    private trackIdx: number;

    constructor() {
        this.time = 0;
        this.animations = [];
        this.listeners = [];
        this.endListeners = [];
        this.trackTags = [];
        this.updateState = new Map();
        this.trackedMap = new Map();
        this.trackIdx = 0;
        this.requestId = 0;
        this.needUpdate = true;
    }

    public start() {
        this.cancelCurrentRequest();
        this.requestId = window.requestAnimationFrame(this.firstFrame);

        return this;
    }

    public isStarted() {
        return this.requestId !== 0;
    }

    public stop() {
        this.cancelCurrentRequest();

        return this;
    }

    public forceUpdate() {
        this.needUpdate = true;

        return this;
    }

    public onUpdate(listener: UpdateListener) {
        this.listeners.push(listener);

        return () => {
            const index = this.listeners.indexOf(listener);

            if (index !== -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    public anim(
        value = DEFAULT_ANIMATION_VALUE,
        duration = DEFAULT_ANIMATION_DURATION,
    ) {
        const animation = new Animation(this, value, duration);

        this.animations.push(animation);

        this.trackTags.forEach((key) => {
            this.trackedMap.get(key)?.push(animation);
        });

        return animation;
    }

    public track(tag?: string) {
        tag = tag ?? `track-${this.trackIdx++}`;

        if (!this.trackedMap.has(tag)) {
            this.trackedMap.set(tag, []);
        }

        this.trackTags.push(tag);

        return this;
    }

    public untrack(tag?: string) {
        if (tag) {
            const index = this.trackTags.indexOf(tag);

            if (index !== -1) {
                this.trackTags.splice(index, 1);
            }

            return tag;
        } else if (this.trackTags.length > 0) {
            return this.trackTags.pop();
        }
    }

    public tracked(tag: string) {
        return this.trackedMap.get(tag) ?? [];
    }

    public clean(trackKey: string): this;
    public clean(animations: Animation[]): this;
    public clean(animationsOrTag: Animation[] | string) {
        if (Array.isArray(animationsOrTag)) {
            this.animations = this.animations.filter(
                (animation) => !animationsOrTag.includes(animation),
            );
        } else {
            const tracked = this.trackedMap.get(animationsOrTag);

            if (tracked) {
                this.animations = this.animations.filter(
                    (animation) => !tracked.includes(animation),
                );
                this.trackedMap.delete(animationsOrTag);
            }
        }

        return this;
    }

    private cancelCurrentRequest() {
        if (this.requestId) {
            window.cancelAnimationFrame(this.requestId);
            this.requestId = 0;
        }
    }

    private firstFrame = (time: number) => {
        this.time = time;
        this.requestId = window.requestAnimationFrame(this.render);
    };

    private render = (time: number) => {
        this.time = time;

        let updated = this.needUpdate;

        for (const animation of this.animations) {
            if (animation.update()) {
                updated = true;
                this.updateState.set(animation, true);
                animation.updateListeners.forEach((cb) => cb(animation.value));

                if (animation.isFinished && animation.onEnd) {
                    animation.value = animation.toValue;
                    this.endListeners.push(animation.onEnd);
                }
            }
        }

        this.listeners.forEach((cb) => cb(updated, this.updateState));
        this.needUpdate = false;
        this.endListeners.forEach((cb) => cb());
        this.updateState.clear();
        this.endListeners.length = 0;
        this.requestId = window.requestAnimationFrame(this.render);
    };
}

export default Animator;
